import * as PIXI from "@pixi/assets";
import { symbolData } from "./SymbolData.js";
import { getAssetsPath, getBasePath } from "./utils/pathConfig";
import { FEATURES_CONFIG } from "./featuresConfig";
import { Settings } from "./settings";

// Manifests — import secondary theme manifest (primary uses spritesheet)
import secondaryManifest from "./manifests/secondary.json";

// Use centralized path configuration
const BASE_PATH = getAssetsPath();
const SPRITESHEETS_PATH = `${getBasePath()}/assets/spritesheets`;

// Generate symbol spine animations from symbol data
// Only include symbols that have spine files (spine property is not empty or null)
const symbolSpines = Object.keys(symbolData)
  .filter((key) => {
    const symbol = symbolData[key];
    return (
      symbol &&
      symbol.spine !== null &&
      symbol.spine !== undefined &&
      symbol.spine !== "" &&
      typeof symbol.spine === "string"
    );
  })
  .map((key) => {
    const spineName = symbolData[key].spine;
    if (
      spineName.includes("<") ||
      spineName.includes(">") ||
      spineName.includes("DOCTYPE") ||
      spineName.includes("html") ||
      spineName.includes("window") ||
      spineName.includes("response") ||
      spineName.trim() !== spineName ||
      spineName.length === 0
    ) {
      return null;
    }
    return {
      name: spineName,
      json: `${BASE_PATH}/spines/reel_symbol/${spineName}.json`,
    };
  })
  .filter((spine) => spine !== null)
  .filter(
    (spine, index, self) =>
      index === self.findIndex((s) => s.name === spine.name)
  );

// ============================================================================
// UI THEME - Dynamic asset loading based on Settings.APP.UI_THEME
// ============================================================================
const UI_THEME = Settings.APP.UI_THEME;

// Build UI assets based on theme
// Primary: single spritesheet (user_interface_primary.json + .png)
// Secondary: individual files from manifest
let UI_ASSETS;
if (UI_THEME === "primary") {
  UI_ASSETS = [{
    name: "ui_spritesheet",
    url: `${SPRITESHEETS_PATH}/user_interface_primary/user_interface_primary.json`,
  }];
} else {
  const UI_PATH = `${BASE_PATH}/user-interface-${UI_THEME}`;
  const uiManifest = secondaryManifest || [];
  UI_ASSETS = uiManifest.map((filename) => ({
    name: filename.replace(".webp", ""),
    url: `${UI_PATH}/${filename}`,
  }));
}

export { UI_ASSETS as UI_ICONS };

/**
 * Get a UI texture from the primary spritesheet by name.
 * Falls back to direct PIXI.Assets.get for non-spritesheet themes.
 * @param {string} name - Asset name (e.g., "icon_home", "popup_button")
 * @returns {PIXI.Texture}
 */
export const getUITexture = (name) => {
  if (UI_THEME === "primary") {
    const key = ASSET_NAME.ui_spritesheet;
    if (!PIXI.Assets.cache.has(key)) return null;
    const sheet = PIXI.Assets.get(key);
    if (sheet?.textures) {
      return sheet.textures[`${name}.png`] || null;
    }
    return null;
  }
  // Secondary theme: textures loaded individually
  return PIXI.Assets.get(name) || null;
};

/**
 * Get a UI texture as a base64 data URL (for use in HTML <img> tags).
 * Extracts the sub-texture from the spritesheet onto a canvas.
 * @param {string} name - Asset name (e.g., "icon_home")
 * @returns {string} data URL or empty string
 */
export const getUITextureAsDataURL = (name) => {
  const texture = getUITexture(name);
  if (!texture) return "";
  const frame = texture.frame;
  const canvas = document.createElement("canvas");
  canvas.width = frame.width;
  canvas.height = frame.height;
  const ctx = canvas.getContext("2d");
  const source = texture.baseTexture.resource.source;
  ctx.drawImage(source, frame.x, frame.y, frame.width, frame.height, 0, 0, frame.width, frame.height);
  return canvas.toDataURL("image/png");
};

// ============================================================================
// GAME TEXTURES - Background, logos, symbols, and game-specific assets
// ============================================================================
const GAME_TEXTURES = [

  // Logo (unified — same image for portrait and landscape)
  {
    name: "logo",
    url: `${BASE_PATH}/images/logo.webp`,
  },

  // Game-specific UI elements
  {
    name: "info_frame",
    url: `${BASE_PATH}/images/info_frame.webp`,
  },

  // Base game background quadrants (split 3840x2580 into 4x 1920x1290 pieces)
  {
    name: "base_top_left",
    url: `${BASE_PATH}/images/base_top_left.webp`,
  },
  {
    name: "base_top_right",
    url: `${BASE_PATH}/images/base_top_right.webp`,
  },
  {
    name: "base_bottom_left",
    url: `${BASE_PATH}/images/base_bottom_left.webp`,
  },
  {
    name: "base_bottom_right",
    url: `${BASE_PATH}/images/base_bottom_right.webp`,
  },
  // Feature game background quadrants (only loaded when Hold and Win is enabled)
  ...(FEATURES_CONFIG.HOLD_AND_WIN.ENABLED ? [
    {
      name: "feature_top_left",
      url: `${BASE_PATH}/images/feature_top_left.webp`,
    },
    {
      name: "feature_top_right",
      url: `${BASE_PATH}/images/feature_top_right.webp`,
    },
    {
      name: "feature_bottom_left",
      url: `${BASE_PATH}/images/feature_bottom_left.webp`,
    },
    {
      name: "feature_bottom_right",
      url: `${BASE_PATH}/images/feature_bottom_right.webp`,
    },
  ] : []),



  // Special symbols (not used in this game)
  // {
  //   name: "special_symbol_1",
  //   url: `${BASE_PATH}/images/special_symbol_1.webp`,
  // },
  // {
  //   name: "special_symbol_2",
  //   url: `${BASE_PATH}/images/special_symbol_2.webp`,
  // },
  // Paylines - Portrait (not used in this game)
  // {
  //   name: "paylines_01_portrait",
  //   url: `${BASE_PATH}/images/paylines_01_portrait.webp`,
  // },
  // {
  //   name: "paylines_02_portrait",
  //   url: `${BASE_PATH}/images/paylines_02_portrait.webp`,
  // },
  // Paylines - Landscape (not used in this game)
  // {
  //   name: "paylines_01_landscape",
  //   url: `${BASE_PATH}/images/paylines_01_landscape.webp`,
  // },
  // {
  //   name: "paylines_02_landscape",
  //   url: `${BASE_PATH}/images/paylines_02_landscape.webp`,
  // },
  {
    name: "reel_symbols",
    url: `${SPRITESHEETS_PATH}/reel_symbols/reel_symbols.json`,
  },
  {
    name: "reel_symbols_blurred",
    url: `${SPRITESHEETS_PATH}/reel_symbols_blurred/reel_symbols_blurred.json`,
  },
  // Reel frame statics (not used - using spine animations instead)
  // {
  //   name: "reel_frame_back_static",
  //   url: `${BASE_PATH}/images/reel_frame_back_static.webp`,
  // },
  // {
  //   name: "reel_frame_back_static_feature",
  //   url: `${BASE_PATH}/images/reel_frame_back_static_feature.webp`,
  // },
  // {
  //   name: "reel_frame_front_static",
  //   url: `${BASE_PATH}/images/reel_frame_front_static.webp`,
  // },

  // Feature Graphics for Paytable (only loaded when Hold and Win is enabled)
  ...(FEATURES_CONFIG.HOLD_AND_WIN.ENABLED ? [
    {
      name: "feature_01_graphics",
      url: `${BASE_PATH}/images/feature_01_graphics.webp`,
    },
    {
      name: "feature_02_graphics",
      url: `${BASE_PATH}/images/feature_02_graphics.webp`,
    },
    {
      name: "feature_03_graphics",
      url: `${BASE_PATH}/images/feature_03_graphics.webp`,
    },
  ] : []),

  // Buy Bonus feature graphics (only loaded when Bonus Buy is enabled)
  ...(FEATURES_CONFIG.BONUS_BUY.ENABLED ? [
    {
      name: "free_spin_feature_graphics",
      url: `${BASE_PATH}/images/free_spin_feature_graphics.webp`,
    },
  ] : []),
  // Shine (not used in this game)
  // {
  //   name: "shine",
  //   url: `${BASE_PATH}/images/shine.webp`,
  // },
];

// ============================================================================
// CONDITIONAL SPINE LOADING - Build spines array based on enabled features
// ============================================================================
const buildSpineAnimations = () => {
  const spines = [];

  // ========================================================================
  // CORE SPINES - Always loaded
  // ========================================================================
  spines.push(
    {
      name: "background_animation",
      json: `${BASE_PATH}/spines/background_animation/background_animation.json`,
    },
    {
      name: "win_celebration",
      json: `${BASE_PATH}/spines/win_celebration/win_celebration.json`,
    },
    {
      name: "intro_screen",
      json: `${BASE_PATH}/spines/intro_screen/intro_screen.json`,
    },

    {
      name: "reel_background",
      json: `${BASE_PATH}/spines/reel_background/reel_background.json`,
    }
  );

  // ========================================================================
  // IOS FULLSCREEN OVERLAY SPINES
  // ========================================================================
  if (FEATURES_CONFIG.IOS_FULLSCREEN_OVERLAY.ENABLED) {
    spines.push({
      name: "ios_scroll",
      json: `${BASE_PATH}/spines/ios_scroll/ios_scroll.json`,
    });
  }

  // ========================================================================
  // DIVIDERS FEATURE SPINES
  // ========================================================================
  if (FEATURES_CONFIG.DIVIDERS.ENABLED) {
    spines.push({
      name: "divider",
      json: `${BASE_PATH}/spines/divider/divider.json`,
    });
  }

  // ========================================================================
  // EXPANDING WILDS FEATURE SPINES
  // ========================================================================
  if (FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel") {
    spines.push({
      name: "wild_expand",
      json: `${BASE_PATH}/spines/wild_expand/wild_expand.json`,
    });
  }

  // ========================================================================
  // TENSION FEATURE SPINES
  // ========================================================================
  if (FEATURES_CONFIG.TENSION.ENABLED) {
    spines.push({
      name: "tension",
      json: `${BASE_PATH}/spines/tension/tension.json`,
    });
  }

  // ========================================================================
  // FREE SPIN TRANSITION & SYMBOL FLIP SPINES
  // ========================================================================
  if (FEATURES_CONFIG.FREE_SPINS.ENABLED) {
    spines.push(
      {
        name: "free_spin_transition",
        json: `${BASE_PATH}/spines/free_spin_transition/free_spin_transition.json`,
      },
      {
        name: "free_spin_counter",
        json: `${BASE_PATH}/spines/free_spin_counter/free_spin_counter.json`,
      },
      {
        name: "symbol_flip",
        json: `${BASE_PATH}/spines/symbol_flip/symbol_flip.json`,
      }
    );
  }

  // ========================================================================
  // WIN LINES FEATURE SPINES
  // ========================================================================
  if (FEATURES_CONFIG.WIN_LINES.ENABLED) {
    spines.push({
      name: "win_lines",
      json: `${BASE_PATH}/spines/win_lines/win_lines.json`,
    });
  }

  // ========================================================================
  // WIN BOX FEATURE SPINES
  // ========================================================================
  if (FEATURES_CONFIG.WIN_BOX.ENABLED) {
    spines.push({
      name: "win_box",
      json: `${BASE_PATH}/spines/win_box/win_box.json`,
    });
  }

  // ========================================================================
  // WIN LINE DIGIT FEATURE SPINES
  // ========================================================================
  if (FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED) {
    spines.push({
      name: "winline_digit",
      json: `${BASE_PATH}/spines/winline_digit/winline_digit.json`,
    });
  }

  // ========================================================================
  // HOLD AND WIN FEATURE SPINES
  // ========================================================================
  if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
    spines.push(
      {
        name: "transition_eye",
        json: `${BASE_PATH}/spines/transition/transition_eye.json`,
      },
      {
        name: "hns_counter",
        json: `${BASE_PATH}/spines/hns_counter/hns_counter.json`,
      },
      {
        name: "hns_multiplier",
        json: `${BASE_PATH}/spines/hns_multiplier/hns_multiplier.json`,
      },
      {
        name: "hns_collector",
        json: `${BASE_PATH}/spines/hns_collector/hns_collector.json`,
      },
      {
        name: "hns_coin_collect",
        json: `${BASE_PATH}/spines/hns_coin_collect/hns_coin_collect.json`,
      },
      {
        name: "hns_end",
        json: `${BASE_PATH}/spines/hns_end/hns_end.json`,
      },
      {
        name: "hns_progress",
        json: `${BASE_PATH}/spines/hns_progress/hns_progress.json`,
      },
      {
        name: "hns_progress_multiplier",
        json: `${BASE_PATH}/spines/hns_progress_multiplier/hns_progress_multiplier.json`,
      },
      {
        name: "hns_progress_tension",
        json: `${BASE_PATH}/spines/hns_progress_tension/hns_progress_tension.json`,
      },
      {
        name: "hns_intro",
        json: `${BASE_PATH}/spines/hns_intro/hns_intro.json`,
      },
      {
        name: "hns_reload",
        json: `${BASE_PATH}/spines/hns_reload/hns_reload.json`,
      },
      {
        name: "total_win",
        json: `${BASE_PATH}/spines/total_win/total_win.json`,
      }
    );
  }

  // ========================================================================
  // MAX WIN — animations are bundled into win_celebration spine (no separate asset needed)
  // ========================================================================

  // ========================================================================
  // FREE SPINS FEATURE SPINES
  // ========================================================================
  if (FEATURES_CONFIG.FREE_SPINS.ENABLED) {
    spines.push({
      name: "dialogs",
      json: `${BASE_PATH}/spines/dialogs/dialogs.json`,
    });
  }

  // ========================================================================
  // BONUS BUY FEATURE SPINES
  // ========================================================================
  if (FEATURES_CONFIG.BONUS_BUY.ENABLED) {
    spines.push({
      name: "bonus_buy",
      json: `${BASE_PATH}/spines/bonus_buy/bonus_buy.json`,
    });
    spines.push({
      name: "bonus_buy_popup",
      json: `${BASE_PATH}/spines/bonus_buy_popup/bonus_buy_popup.json`,
    });
  }

  // ========================================================================
  // PRIMARY UI SPINES
  // ========================================================================
  if (UI_THEME === "primary") {
    spines.push({
      name: "loading_screen",
      json: `${BASE_PATH}/spines/loading_screen/loading_screen.json`,
    });
  }

  // ========================================================================
  // SYMBOL SPINES - Always include (dynamically generated from SymbolData)
  // ========================================================================
  spines.push(...symbolSpines);

  return spines;
};

const SPINE_ANIMATIONS = buildSpineAnimations();

// ============================================================================
// BITMAP FONTS - Font assets
// ============================================================================
const buildBitmapFonts = () => {
  const fonts = [];

  // Core font - always loaded
  fonts.push({
    name: "font",
    json: `${BASE_PATH}/fonts/font/font.fnt`,
  });

  // Blurred font - only for Hold and Win feature
  if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
    fonts.push({
      name: "font_blurred",
      json: `${BASE_PATH}/fonts/font/font_blurred.fnt`,
    });
  }

  return fonts;
};

const BITMAP_FONTS = buildBitmapFonts();

// ============================================================================
// MAIN ASSETS OBJECT - Combines all asset categories
// ============================================================================
export const ASSETS = {
  bitmapFonts: BITMAP_FONTS,
  spineAnimations: SPINE_ANIMATIONS,
  textures: [...UI_ASSETS, ...GAME_TEXTURES],
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generates a flat object mapping asset names to their URLs/JSON paths
 * @param {Object} assets - Assets object with textures, spineAnimations, and bitmapFonts
 * @returns {Object} Flat object with asset names as keys and URLs/JSON paths as values
 */
const generateAssetNames = (assets) => {
  const assetNames = {};

  // Add textures
  assets.textures.forEach((asset) => {
    assetNames[asset.name] = asset.url;
  });

  // Add spine animations
  assets.spineAnimations.forEach((asset) => {
    assetNames[asset.name] = asset.json;
  });

  // Add bitmap fonts
  assets.bitmapFonts.forEach((asset) => {
    assetNames[asset.name] = asset.json;
  });

  return assetNames;
};

/**
 * Gets an asset from PIXI Assets cache if loading succeeded
 * @param {string} status - Loading status
 * @param {string} assetName - Name of the asset to retrieve
 * @returns {*} The asset if succeeded, null otherwise
 */
export const getAsset = (status, assetName) =>
  status === "succeeded" ? PIXI.Assets.get(assetName) : null;

// Generate the asset name mapping
export const ASSET_NAME = generateAssetNames(ASSETS);
